import React, { createContext, useContext, useEffect, useState } from "react";
import { authService, setGlobalLogoutCallback } from "@/services/auth";
import { User } from "@/types";
import { apiCache } from "@/utils/api-cache";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<import("@/types").AuthResponse>;
  logout: () => void;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Register global logout callback for API interceptor
    setGlobalLogoutCallback(() => {
      setUser(null);
    });

    // Initialize auth immediately - localStorage is always available in useEffect
    const initAuth = async () => {
      try {
        // Check if user is already logged in
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Cleanup
    return () => {
      setGlobalLogoutCallback(() => {});
    };
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);

    // Cognito challenge (e.g. invited staff must set a new password): no
    // tokens yet — hand the challenge back to the login screen untouched.
    if (response.challengeName) {
      return response;
    }

    // Check if user has a portal role (physician = MD reviewer)
    if (
      !authService.hasRole("pharmacist") &&
      !authService.hasRole("physician") &&
      !authService.hasRole("admin")
    ) {
      authService.logout();
      throw new Error(
        "You do not have permission to access the pharmacy portal."
      );
    }

    setUser(authService.getCurrentUser());
    return response;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    // Clear all cached API data on logout
    apiCache.clear();
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
